/**
 * Local Squad — discovery + create.
 * Embedded in Community.jsx "Local" tab.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, MapPin, Plus, X } from "lucide-react";
import { apiClient } from "../../services/api";
import { toast } from "sonner";

export default function LocalSquad() {
  const navigate = useNavigate();
  const [data, setData] = useState({ squads: [], city: "" });
  const [creating, setCreating] = useState(false);

  const load = () => apiClient.get("/community/squads/discover").then((r) => setData(r.data || { squads: [] }));
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="local-squad-tab" className="px-1">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 4px 12px" }}>
        <div>
          <div style={{ fontFamily: "Barlow Condensed", fontSize: 22, fontWeight: 800, lineHeight: 1.05 }}>Local squads</div>
          <div style={{ fontSize: 12, color: "var(--text-tertiary, #8A8884)", marginTop: 2 }}>Hyperlocal fitness groups in {data.city || "your city"}</div>
        </div>
        <button onClick={() => setCreating(true)} data-testid="create-squad-btn"
          style={{ padding: "8px 14px", background: "#0C0C0B", color: "white", border: "none", borderRadius: 100, fontSize: 12, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>
          <Plus size={12} /> New
        </button>
      </div>
      {data.squads.length === 0 ? (
        <div style={{ textAlign: "center", padding: "30px 16px" }}>
          <Users size={32} style={{ margin: "0 auto", color: "var(--text-tertiary, #8A8884)" }} />
          <div style={{ fontFamily: "Barlow Condensed", fontSize: 18, fontWeight: 800, marginTop: 8 }}>No squads yet in {data.city}</div>
          <p style={{ fontSize: 12, color: "var(--text-tertiary, #8A8884)", maxWidth: 280, margin: "6px auto 0" }}>Be the first to start a fitness squad in your neighbourhood.</p>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          {data.squads.map((s) => (
            <button key={s.squad_id} onClick={() => navigate(`/community/squad/${s.squad_id}`)} data-testid={`squad-card-${s.squad_id}`}
              style={{ textAlign: "left", padding: 14, background: "var(--bg-surface, #E4E2DE)", border: "1px solid var(--border, rgba(12,12,11,0.10))", borderRadius: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: "Barlow Condensed", fontSize: 18, fontWeight: 800 }}>{s.name}</div>
                  <div style={{ fontSize: 11, color: "var(--text-tertiary, #8A8884)", display: "inline-flex", alignItems: "center", gap: 4 }}>
                    <MapPin size={10} /> {s.neighborhood}
                  </div>
                </div>
                {s.is_member ? (
                  <span style={{ padding: "3px 8px", background: "rgba(34,197,94,0.10)", color: "#16A34A", fontSize: 10, fontWeight: 700, borderRadius: 100, textTransform: "uppercase", letterSpacing: "0.06em" }}>Member</span>
                ) : (
                  <span style={{ padding: "3px 8px", background: "var(--bg-inset, #D8D6D1)", color: "var(--text-secondary, #4A4845)", fontSize: 10, fontWeight: 700, borderRadius: 100 }}>{s.member_count} members</span>
                )}
              </div>
              {s.description && <p style={{ fontSize: 12, color: "var(--text-secondary, #4A4845)", marginTop: 6 }}>{s.description}</p>}
            </button>
          ))}
        </div>
      )}
      {creating && <CreateSquadModal onClose={() => setCreating(false)} onCreated={(s) => { setCreating(false); load(); navigate(`/community/squad/${s.squad_id}`); }} />}
    </div>
  );
}

function CreateSquadModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [neighborhood, setNeighborhood] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!name || !neighborhood) return toast.error("Name and neighbourhood are required");
    setBusy(true);
    try {
      const { data } = await apiClient.post("/community/squads", { name, neighborhood, description: description || null });
      toast.success("Squad created · +50 FP");
      onCreated(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[80] grid place-items-end bg-black/50" onClick={onClose} data-testid="create-squad-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full bg-white rounded-t-[20px] p-6">
        <div className="flex justify-between items-center" style={{ display: "flex", justifyContent: "space-between" }}>
          <h3 style={{ fontFamily: "Barlow Condensed", fontSize: 22, fontWeight: 800 }}>New Squad</h3>
          <button onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>
        <div style={{ display: "grid", gap: 10, marginTop: 14 }}>
          <input value={name} onChange={(e) => setName(e.target.value.slice(0, 60))} placeholder="Squad name" data-testid="sqd-name"
            style={{ height: 44, padding: "0 14px", background: "var(--bg-inset, #D8D6D1)", border: "none", borderRadius: 12, fontSize: 14, outline: "none" }} />
          <input value={neighborhood} onChange={(e) => setNeighborhood(e.target.value.slice(0, 80))} placeholder="Neighbourhood (e.g., Bandra West)" data-testid="sqd-hood"
            style={{ height: 44, padding: "0 14px", background: "var(--bg-inset, #D8D6D1)", border: "none", borderRadius: 12, fontSize: 14, outline: "none" }} />
          <textarea value={description} onChange={(e) => setDescription(e.target.value.slice(0, 200))} placeholder="What's this squad about? (optional)" data-testid="sqd-desc" rows={3}
            style={{ padding: 14, background: "var(--bg-inset, #D8D6D1)", border: "none", borderRadius: 12, fontSize: 14, resize: "none", outline: "none" }} />
          <button onClick={submit} disabled={busy} data-testid="sqd-submit"
            style={{ height: 48, background: "#0C0C0B", color: "white", border: "none", borderRadius: 100, fontSize: 14, fontWeight: 800 }}>
            {busy ? "Creating…" : "Create Squad · +50 FP"}
          </button>
        </div>
      </div>
    </div>
  );
}
